import type { RoiDataset, RoiProject, RoiSignalSource } from '../types/roi';

export function deriveDataset(
  base: RoiDataset,
  enabledSources: ReadonlySet<RoiSignalSource>
): RoiDataset {
  const baselineCaught = base.caughtProjects;

  const effectiveProjects: RoiProject[] = base.projects.map((project) => {
    if (project.verdict === 'missed') {
      return project;
    }
    const primarySource = project.signals[0]?.source;
    const stillCaught =
      primarySource !== undefined && enabledSources.has(primarySource);
    if (stillCaught) {
      const visibleSignals = project.signals.filter((signal) =>
        enabledSources.has(signal.source)
      );
      return { ...project, signals: visibleSignals };
    }
    return { ...project, verdict: 'missed', signals: [] };
  });

  const caughtProjects = effectiveProjects.filter(
    (project) => project.verdict === 'caught'
  );
  const captureRate =
    baselineCaught === 0 ? 0 : caughtProjects.length / baselineCaught;

  const scaledHeroTotalUsd = Math.round(base.heroTotalUsd * captureRate);

  const lateProjects = caughtProjects.filter(
    (project) => project.weeksSlipped > 0
  );
  const totalWeeksSlipped = lateProjects.reduce(
    (sum, project) => sum + project.weeksSlipped,
    0
  );
  const worstSlipProject = lateProjects.reduce<RoiProject | null>(
    (worst, project) =>
      worst === null || project.weeksSlipped > worst.weeksSlipped
        ? project
        : worst,
    null
  );

  const firefightProjects = caughtProjects.filter(
    (project) => project.firefightEngineerDays > 0
  );
  const totalFirefightDays = firefightProjects.reduce(
    (sum, project) => sum + project.firefightEngineerDays,
    0
  );
  const p1IncidentCount = Math.round(totalFirefightDays * 0.18);
  const hotfixPrCount = Math.round(totalFirefightDays * 0.42);
  const escalationMeetingsCount = Math.round(totalFirefightDays * 0.24);

  const scaledMonthlyCumulative = base.monthlyCumulative.map((point) => ({
    monthLabel: point.monthLabel,
    monthlyUsd: Math.round(point.monthlyUsd * captureRate),
    cumulativeUsd: Math.round(point.cumulativeUsd * captureRate),
  }));

  return {
    ...base,
    heroTotalUsd: scaledHeroTotalUsd,
    caughtProjects: caughtProjects.length,
    missedProjects: base.totalProjects - caughtProjects.length,
    buckets: {
      rework: {
        ...base.buckets.rework,
        formula: {
          ...base.buckets.rework.formula,
          hoursPerPersonPerMonth:
            Math.round(
              base.buckets.rework.formula.hoursPerPersonPerMonth *
                captureRate *
                10
            ) / 10,
          totalUsd: scaledHeroTotalUsd,
        },
      },
      late: {
        ...base.buckets.late,
        projectsLate: lateProjects.length,
        totalWeeksSlipped,
        worstSlipWeeks: worstSlipProject?.weeksSlipped ?? 0,
        worstSlipProject: worstSlipProject?.name ?? '-',
      },
      firefight: {
        ...base.buckets.firefight,
        projectsWithFirefight: firefightProjects.length,
        p1IncidentCount,
        hotfixPrCount,
        escalationMeetingsCount,
      },
    },
    monthlyCumulative: scaledMonthlyCumulative,
    projects: effectiveProjects,
  };
}
